'use client';

import { Button, createListCollection, Dialog, Flex, Input, Portal, Text } from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';

import PhoneInput from '@components/common/PhoneInput';
import SelectField from '@features/spend/SelectField';
import { SUPPLIER_PAYOUT_NETWORK_LABEL } from '@features/finance/financeMeta';

interface Props {
  open: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  initialName?: string;
}

const inputProps = {
  borderColor: 'border.light',
  borderRadius: 'md',
  size: 'sm' as const,
};

const networkCollection = createListCollection({
  items: Object.entries(SUPPLIER_PAYOUT_NETWORK_LABEL).map(([value, label]) => ({ value, label })),
});

// UI-only: collects supplier details + optional payment info, then closes.
const NewSupplierDialog = memo(({ open, onOpenChange, initialName = '' }: Props) => {
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(initialName);
      setPhone('');
    }
  }

  const onChangeName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value), []);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} placement="center">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl">
            <Dialog.Header>
              <Dialog.Title>New supplier</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Flex direction="column" gap={3}>
                <Flex direction="column" gap={1}>
                  <Text fontSize="xs" color="text.secondary">
                    Supplier name
                  </Text>
                  <Input placeholder="Supplier name" value={name} onChange={onChangeName} {...inputProps} />
                </Flex>
                <Flex direction="column" gap={1}>
                  <Flex justifyContent="space-between" alignItems="baseline">
                    <Text fontSize="xs" color="text.secondary">
                      Phone
                    </Text>
                    <Text fontSize="2xs" color="text.secondary">
                      Optional
                    </Text>
                  </Flex>
                  <PhoneInput value={phone} onChange={setPhone} size="sm" />
                </Flex>
                <Flex direction="column" gap={1}>
                  <Flex justifyContent="space-between" alignItems="baseline">
                    <Text fontSize="xs" color="text.secondary">
                      Email
                    </Text>
                    <Text fontSize="2xs" color="text.secondary">
                      Optional
                    </Text>
                  </Flex>
                  <Input type="email" placeholder="name@example.com" {...inputProps} />
                </Flex>
                <Text fontSize="xs" fontWeight="medium" color="text.secondary" mt={1}>
                  Payment info (optional) — autofills payment links
                </Text>
                <Flex direction="column" gap={1}>
                  <Text fontSize="xs" color="text.secondary">
                    Network
                  </Text>
                  <SelectField collection={networkCollection} placeholder="Select network" />
                </Flex>
                <Flex direction="column" gap={1}>
                  <Text fontSize="xs" color="text.secondary">
                    Wallet address
                  </Text>
                  <Input placeholder="0x… or G…" {...inputProps} />
                </Flex>
                <Flex direction="column" gap={1}>
                  <Text fontSize="xs" color="text.secondary">
                    Memo / tag
                  </Text>
                  <Input placeholder="If the network needs one" {...inputProps} />
                </Flex>
              </Flex>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" size="xs" borderRadius="md">
                  Cancel
                </Button>
              </Dialog.ActionTrigger>
              <Dialog.ActionTrigger asChild>
                <Button size="xs" borderRadius="md">
                  Add supplier
                </Button>
              </Dialog.ActionTrigger>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
NewSupplierDialog.displayName = 'NewSupplierDialog';

export default NewSupplierDialog;
